<template>
  <section class="planned-visit-date-editor" data-cy="planned-visit-date-editor">
    <h2>Planned visit date</h2>
    <p v-if="!canEdit" class="planned-visit-date-editor__message">
      Planned visit date cannot be changed for this visit.
    </p>
    <template v-else>
      <label for="plant-visit-planned-date">Select a future date</label>
      <DatePicker
        id="plant-visit-planned-date"
        :value="plannedDate ?? undefined"
        :min="minimumPlannedDate"
        format="MM/dd/yyyy"
        :disabled="loading"
        :popup-settings="{ popupClass: 'modal-popup' }"
        @change="(...args: unknown[]) => onDateChanged(args[0])"
        data-cy="plant-visit-planned-date-picker"
      />
      <Button
        type="button"
        theme-color="primary"
        :disabled="loading"
        @click="saveDateAsync"
        data-cy="plant-visit-save-planned-date-button"
      >
        Save
      </Button>
    </template>
  </section>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { DatePicker } from "@progress/kendo-vue-dateinputs";
import { Button } from "@progress/kendo-vue-buttons";
import { api } from "@/api";
import { handleResponsePromiseAsync } from "@/shared/helpers/apiHelper";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";

const props = defineProps<{
  visitId: number;
  plannedVisitDate: string | null;
  canEdit: boolean;
}>();

const emit = defineEmits<{
  (event: "dateUpdated", plannedDate: string | null): void;
}>();

const notificationStore = useNotificationStore();
const loading = ref(false);
const plannedDate = ref<Date | null>(
  props.plannedVisitDate ? new Date(props.plannedVisitDate) : null
);
const minimumPlannedDate = new Date();
minimumPlannedDate.setHours(0, 0, 0, 0);

const formatDateForApi = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const onDateChanged = (event: unknown) => {
  plannedDate.value = (event as { value?: Date }).value ?? null;
};

const saveDateAsync = async () => {
  if (loading.value || !props.canEdit) return;

  if (plannedDate.value && plannedDate.value < minimumPlannedDate) {
    notificationStore.notify({
      type: NotificationTypeEnum.error,
      message: "Visit planned date cannot be in the past",
      duration: 5000,
    });
    return;
  }

  loading.value = true;
  const plannedDateValue = plannedDate.value ? formatDateForApi(plannedDate.value) : null;

  await handleResponsePromiseAsync(
    api.post("UpdateVisitPlannedDate", {
      VisitId: props.visitId,
      PlannedVisitDate: plannedDateValue,
    }),
    (success) => {
      if (!success) {
        notificationStore.notify({
          type: NotificationTypeEnum.error,
          message: "Error updating visit planned date",
          duration: 5000,
        });
        return;
      }

      notificationStore.notify({
        type: NotificationTypeEnum.success,
        message: "Visit planned date updated successfully",
        duration: 5000,
      });
      emit("dateUpdated", plannedDateValue);
    },
    "PlannedVisitDateEditor.vue",
    "saveDateAsync",
    () => {
      notificationStore.notify({
        type: NotificationTypeEnum.error,
        message: "Error updating visit planned date",
        duration: 5000,
      });
    }
  );

  loading.value = false;
};
</script>

<style scoped lang="scss">
.planned-visit-date-editor {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-self: start;
  padding: 24px;
  background: #f8fafc;
  border: 1px solid #dbe4f0;
  border-radius: 12px;

  h2 {
    margin: 0;
  }

  label {
    color: #475569;
    font-size: 0.9rem;
    font-weight: 600;
  }

  button {
    align-self: flex-end;
    margin-top: 8px;
  }

  &__message {
    margin: 0;
    color: #64748b;
  }
}
</style>